export interface DisplayPlayer {
  id: string;
  name: string;
  score: number;
  avatar?: string;
  joinedAt: number;
  isHost?: boolean;
}

export interface GameLeaderboardEntry {
  playerId: string;
  playerName: string;
  score: number;
}

export interface GameWithLeaderboard {
  playedAt: number;
  leaderboard: GameLeaderboardEntry[];
}

function playerKey(name: string, id: string): string {
  const normalizedName = name?.trim().toUpperCase();
  if (normalizedName && normalizedName.length > 0) {
    return `name:${normalizedName}`;
  }
  return `id:${id}`;
}

/**
 * Merge room players with saved game statistics for admin display.
 * Same display name (e.g. after reconnect) becomes one row; session scores sum across games.
 */
export function mergePlayersForDisplay(
  roomPlayers: DisplayPlayer[],
  statisticsGames: GameWithLeaderboard[],
  hostId?: string
): DisplayPlayer[] {
  const sessionScoresByKey = new Map<string, number>();

  for (const game of statisticsGames) {
    for (const entry of game.leaderboard) {
      const key = playerKey(entry.playerName, entry.playerId);
      sessionScoresByKey.set(key, (sessionScoresByKey.get(key) ?? 0) + entry.score);
    }
  }

  const byKey = new Map<string, DisplayPlayer>();

  const mergeEntry = (candidate: DisplayPlayer) => {
    const key = playerKey(candidate.name, candidate.id);
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, { ...candidate });
      return;
    }

    const shouldReplace =
      candidate.score > existing.score ||
      (candidate.score === existing.score && candidate.joinedAt > existing.joinedAt);

    const preferred = shouldReplace ? candidate : existing;
    const other = shouldReplace ? existing : candidate;

    byKey.set(key, {
      ...preferred,
      joinedAt: Math.min(existing.joinedAt, candidate.joinedAt),
      avatar: preferred.avatar ?? other.avatar,
      isHost: preferred.isHost || other.isHost,
      score: Math.max(existing.score, candidate.score),
    });
  };

  for (const player of roomPlayers) {
    mergeEntry(player);
  }

  for (const game of statisticsGames) {
    for (const entry of game.leaderboard) {
      mergeEntry({
        id: entry.playerId,
        name: entry.playerName,
        score: entry.score,
        joinedAt: game.playedAt,
      });
    }
  }

  return Array.from(byKey.values()).map((player) => {
    const key = playerKey(player.name, player.id);
    const sessionScore = sessionScoresByKey.get(key);
    return {
      ...player,
      score: statisticsGames.length > 0 ? (sessionScore ?? player.score) : player.score,
      isHost: player.isHost ?? player.id === hostId,
    };
  });
}

export function countUniquePlayersFromGames(statisticsGames: GameWithLeaderboard[]): number {
  const names = new Set<string>();
  for (const game of statisticsGames) {
    for (const entry of game.leaderboard) {
      const name = entry.playerName?.trim().toUpperCase();
      if (name) names.add(name);
    }
  }
  return names.size;
}
